import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Star, Camera, Heart, Sparkles } from "lucide-react";
import api from "../lib/api";
import { useT, useUI, useLang } from "../lib/i18n";
import SEO from "../components/site/SEO";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } },
};

export default function Home() {
  const { content } = useOutletContext();
  const t = useT();
  const ui = useUI();
  const lang = useLang();
  const [services, setServices] = useState([]);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    api.get("/services").then(({ data }) => setServices(data)).catch(() => {});
    api.get("/testimonials").then(({ data }) => setTestimonials(data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hero = content.hero || {};
  const stats = content.stats || { items: [] };
  const servicesIntro = content.services_intro || {};
  const about = content.about || {};
  const features = content.features || { items: [] };
  const testimonialsIntro = content.testimonials_intro || {};
  const cta = content.cta || {};
  const vis = content.sections_visibility || {};
  const show = (k) => vis[k] !== false;
  const seo = (content.seo || {}).home;

  return (
    <div data-testid="home-page">
      <SEO seoData={seo} />
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-end" data-testid="hero-section">
        <div className="absolute inset-0 overflow-hidden">
          <motion.img
            initial={{ scale: 1.1, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 2, ease: [0.16, 1, 0.3, 1] }}
            src={hero.image_url}
            alt="Hero"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/0 to-[#FDFBF7]"></div>
        </div>
        <div className="relative max-w-7xl mx-auto w-full px-6 md:px-12 pb-16 md:pb-24">
          <motion.div initial="hidden" animate="visible" variants={fadeUp}>
            <p className="overline mb-4 !text-white" style={{ textShadow: "0 1px 8px rgba(0,0,0,0.3)" }}>
              {t(hero.overline)}
            </p>
            <h1 className="font-serif text-5xl sm:text-7xl md:text-8xl tracking-tighter leading-[0.95] font-light text-white max-w-4xl whitespace-pre-line" style={{ textShadow: "0 2px 18px rgba(0,0,0,0.25)" }}>
              {t(hero.title)}
            </h1>
            <p className="mt-8 max-w-xl text-white/90 text-lg leading-relaxed" style={{ textShadow: "0 1px 6px rgba(0,0,0,0.3)" }}>
              {t(hero.subtitle)}
            </p>
            <div className="mt-10">
              <Link to={`/${lang}/contact`} className="btn-primary" data-testid="hero-cta">
                {t(hero.cta_label) || ui.book} <ArrowRight size={18} />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {show("stats") && (
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20" data-testid="stats-section">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#2D2A26]/10 border border-[#2D2A26]/10 rounded-3xl overflow-hidden">
          {stats.items?.map((s, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: i * 0.1 }} className="bg-[#FDFBF7] p-12 text-center">
              <div className="font-serif text-6xl md:text-7xl font-light text-[#C87A5E]">{s.value}<span className="text-[#2D2A26]/40 text-4xl">{s.suffix}</span></div>
              <div className="overline mt-3 !text-[#4A3F35]">{t(s.label)}</div>
            </motion.div>
          ))}
        </div>
      </section>
      )}

      {show("services_preview") && (
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20" data-testid="services-section">
        <div className="grid md:grid-cols-12 gap-12 mb-16">
          <div className="md:col-span-4"><p className="overline mb-4">{t(servicesIntro.overline)}</p></div>
          <div className="md:col-span-8">
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-tight">{t(servicesIntro.title)}</h2>
            <p className="mt-6 text-[#4A3F35] max-w-2xl">{t(servicesIntro.description)}</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {services.map((s, i) => (
            <motion.div key={s.id} initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: (i % 2) * 0.15 }} className={`group relative overflow-hidden rounded-3xl bg-[#F6F3EC] ${i % 3 === 0 ? "md:row-span-2" : ""}`} data-testid={`service-card-${i}`}>
              <div className="aspect-[4/5] overflow-hidden">
                <img src={s.image_url} alt={t(s.title)} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out" />
              </div>
              <div className="absolute inset-x-0 bottom-0 p-8 bg-gradient-to-t from-black/85 via-black/40 to-transparent text-white">
                <div className="flex items-end justify-between gap-4">
                  <div>
                    <span className="font-serif text-2xl text-[#C87A5E]">{s.number}</span>
                    <h3 className="font-serif text-2xl md:text-3xl mt-1">{t(s.title)}</h3>
                    <p className="mt-3 text-sm text-white/80 max-w-sm line-clamp-2">{t(s.short_description)}</p>
                  </div>
                  <Link to={`/${lang}/services`} className="shrink-0 w-12 h-12 rounded-full bg-white/15 backdrop-blur flex items-center justify-center hover:bg-[#C87A5E] transition">
                    <ArrowUpRight size={20} />
                  </Link>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>
      )}

      {show("about_preview") && (
      <section className="bg-[#F6F3EC] py-24" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-16 items-center">
          <motion.div initial={{ opacity: 0, x: -40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.9 }}>
            <div className="rounded-3xl overflow-hidden aspect-[4/5]"><img src={about.image_url} alt={t(about.title)} className="w-full h-full object-cover" /></div>
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.9 }}>
            <p className="overline mb-4">{t(about.overline)}</p>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-tight">{t(about.title)}</h2>
            <p className="mt-6 text-[#4A3F35] leading-relaxed">{t(about.description)}</p>
            <div className="mt-10"><Link to={`/${lang}/a-propos`} className="btn-ghost">{ui.learnMore} <ArrowRight size={16} /></Link></div>
          </motion.div>
        </div>
      </section>
      )}

      {show("features") && (
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24" data-testid="features-section">
        <div className="text-center mb-16">
          <p className="overline mb-4">{t(features.overline)}</p>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tight max-w-3xl mx-auto">{t(features.title)}</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-px bg-[#2D2A26]/10 rounded-3xl overflow-hidden border border-[#2D2A26]/10">
          {features.items?.map((f, i) => {
            const icons = [<Camera size={28} />, <Heart size={28} />, <Sparkles size={28} />];
            return (
              <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: i * 0.1 }} className="bg-[#FDFBF7] p-10">
                <div className="text-[#C87A5E] mb-6">{icons[i] || icons[0]}</div>
                <h3 className="font-serif text-2xl mb-3">{t(f.title)}</h3>
                <p className="text-[#4A3F35] leading-relaxed">{t(f.description)}</p>
              </motion.div>
            );
          })}
        </div>
      </section>
      )}

      {show("testimonials") && (
      <section className="bg-[#2D2A26] text-[#FDFBF7] py-24" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="overline mb-4 !text-[#C87A5E]">{t(testimonialsIntro.overline)}</p>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight">{t(testimonialsIntro.title)}</h2>
            <p className="mt-4 text-[#FDFBF7]/60 max-w-xl mx-auto">{t(testimonialsIntro.description)}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((tm, i) => (
              <motion.div key={tm.id} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: i * 0.1 }} className="bg-[#FDFBF7]/5 border border-[#FDFBF7]/10 rounded-3xl p-8 backdrop-blur">
                <div className="flex gap-1 mb-5">
                  {Array.from({ length: tm.rating || 5 }).map((_, k) => (
                    // Stars are stateless static elements - index key is safe here
                    // eslint-disable-next-line react/no-array-index-key
                    <Star key={`star-${tm.id}-${k}`} size={14} className="fill-[#C87A5E] text-[#C87A5E]" />
                  ))}
                </div>
                <p className="font-serif italic text-xl leading-relaxed">&ldquo;{t(tm.content)}&rdquo;</p>
                <div className="mt-6 pt-6 border-t border-[#FDFBF7]/10">
                  <div className="font-medium">{tm.name}</div>
                  <div className="text-sm text-[#FDFBF7]/50">{t(tm.role)}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      )}

      {show("cta") && (
      <section className="max-w-5xl mx-auto px-6 md:px-12 py-32 text-center" data-testid="cta-section">
        <p className="overline mb-4">{t(cta.overline)}</p>
        <h2 className="font-serif text-4xl md:text-6xl tracking-tighter leading-tight">{t(cta.title)}</h2>
        <p className="mt-6 text-[#4A3F35] max-w-2xl mx-auto">{t(cta.description)}</p>
        <div className="mt-10"><Link to={`/${lang}/contact`} className="btn-primary">{t(cta.cta_label) || ui.contact} <ArrowRight size={18} /></Link></div>
      </section>
      )}
    </div>
  );
}
